import React, { useState, useEffect, useContext } from "react";
import { Table, Button, Modal } from "react-bootstrap";
import Filters from "./Filters";
import { getAuthToken } from "../../../../util/auth";
import { getApiChara } from "../../../../util/getApi";
import { jsPDF } from "jspdf";
import { useNavigate } from "react-router-dom";
import Loader from "../../../UI/Loader";
import { toast } from "react-toastify";
import classes from "./BillStatements.module.css";
import { printBillStatementReceipt, exportBillPDF, shareBillViaWhatsApp } from "./BillStatementReceipt";
import "jspdf-autotable";
import { FaSearch, FaEye, FaPlus, FaFileExport, FaPrint, FaCheck, FaTimes, FaWhatsapp } from "react-icons/fa";
import AuthContext from "../../../../store/auth-context";
import { drawReportHeader, drawAuthorizedSignatory } from "../../../../util/headerUtil";
import { getReportHeader, getReportStyles } from "../../../../util/reportPrintHelper";
import { exportTableToExcel } from "../../../../util/excelExportUtil";
import { Spinner } from "react-bootstrap";
import { themeColors } from "../../../../util/themeColors";
import { useTranslation } from "react-i18next";

const hexToRgb = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
};

const BillStatements = () => {
  const { t } = useTranslation();
  const getTodayDate = () => new Date().toISOString().split("T")[0];

  const authCtx = useContext(AuthContext);
  const hospitalData = authCtx.hospitalData;

  const [selectedPageSize, setSelectedPageSize] = useState("a4");
  const [selectedOrientation, setSelectedOrientation] = useState("portrait");
  const [showPreview, setShowPreview] = useState(false);
  const [startingDate, setStartingDate] = useState(getTodayDate());
  const [endingDate, setEndingDate] = useState(getTodayDate());
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState(null);
  const [outstandingOnly, setOutstandingOnly] = useState(false);
  const [searchPara, setSearchPara] = useState("");
  const [patientData, setPatientData] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [paymentType, setPaymentType] = useState("");
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [selectedService, setSelectedService] = useState("");
  const [exportFormat, setExportFormat] = useState("excel");
  const [isExporting, setIsExporting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setIsError(false);
      try {
        const token = getAuthToken();
        if (!token || token === "EXPIRED") navigate("/");
        const apiChar = getApiChara();
        let apiUrl = "";

        if (outstandingOnly) {
          apiUrl = `${apiChar}/ipd/billing/outstanding/all-patients`;
        } else if (selectedPatient?.patientId) {
          apiUrl = `${apiChar}/ipd/billing/patient/${selectedPatient.patientId}`;
        } else {
          apiUrl = `${apiChar}/ipd/billing/report/today`;
        }

        const response = await fetch(apiUrl, {
          headers: { Authorization: "Bearer " + token },
        });
        if (!response.ok) throw new Error("Network response was not ok");

        const result = await response.json();
        setData(result || []);
      } catch (err) {
        setIsError(true);
        setError(err.message || "An error occurred");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [outstandingOnly, navigate]);

  const onSearchPatientHandler = async (e) => {
    setOutstandingOnly(false);
    e.preventDefault();
    const token = getAuthToken();
    if (!token || token === "EXPIRED") {
      navigate("/");
      return;
    }

    setIsLoading(true);
    try {
      const apiChar = getApiChara();
      const apiUrl =
        `${apiChar}/patient/getPatientByFNameOrLNameOrMobile?value=` +
        searchPara;

      const response = await fetch(apiUrl, {
        headers: { Authorization: "Bearer " + token },
      });
      if (!response.ok) throw new Error("Failed to fetch patient data");

      const data = await response.json();
      if (!data || data.length === 0) {
        toast.info(t("No patients found"));
      }
      setPatientData(data);
    } catch (err) {
      console.error("Search error:", err);
      toast.error(t("Error fetching patients"));
    } finally {
      setIsLoading(false);
    }
  };

  const getSelectedPatientFromList = async (patient) => {
    setSelectedPatient(patient);
    setPatientData([]);

    if (!patient?.patientId) return;

    const token = getAuthToken();
    if (!token || token === "EXPIRED") {
      navigate("/");
      return;
    }

    try {
      setIsLoading(true);
      const apiChar = getApiChara();
      const apiUrl = `${apiChar}/ipd/billing/patient/${patient.patientId}`;
      const response = await fetch(apiUrl, {
        headers: { Authorization: "Bearer " + token },
      });
      if (!response.ok) throw new Error("Failed to fetch billing data");

      const bills = await response.json();
      setData(bills || []);
    } catch (err) {
      console.error("Billing fetch error:", err);
      toast.error(t("Error fetching billing data"));
    } finally {
      setIsLoading(false);
    }
  };

  const onDataCloseHandler = () => {
    setPatientData([]);
  };

  const TableHeader = () => (
    <thead>
      <tr>
        <th>{t("Date")}</th>
        <th>{t("Name")}</th>
        <th>{t("Mobile")}</th>
        <th>{t("Payment Type")}</th>
        <th>{t("Received By")}</th>
        <th>{t("Net Amount")}</th>
        <th>{t("Amount Received")}</th>
        <th>{t("Outstanding Amount")}</th>
        <th>{t("Action")}</th>
      </tr>
    </thead>
  );

  const onGetStartEndDateHandler = (startDate, endDate) => {
    setSearchPara("");
    setOutstandingOnly(false);
    setSelectedPatient(null);
    setStartingDate(startDate);
    setEndingDate(endDate);
    fetchByDate(startDate, endDate);
  };

  const fetchByDate = async (startDate, endDate) => {
    setIsLoading(true);
    setIsError(false);
    try {
      const token = getAuthToken();
      if (!token || token === "EXPIRED") {
        navigate("/");
        return;
      }
      const apiChar = getApiChara();
      const apiUrl = `${apiChar}/ipd/billing/report/date-range?startDate=${startDate}&endDate=${endDate}`;

      const response = await fetch(apiUrl, {
        headers: { Authorization: "Bearer " + token },
      });
      if (!response.ok) throw new Error("Failed to fetch report data");

      const result = await response.json();
      setData(result || []);
    } catch (err) {
      setIsError(true);
      setError(err.message || "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const uniqueDoctors = Array.from(
    new Set(
      (data || [])
        .map(
          (billing) =>
            `${billing?.billReceivedById?.firstName || ""} ${billing?.billReceivedById?.lastName || ""
              }`.trim()
        )
        .filter((name) => name !== "")
    )
  );

  const uniqueServices = Array.from(
    new Set(
      (data || [])
        .flatMap((billing) => billing?.serviceItems || [])
        .map((item) => item?.serviceDescription?.trim())
        .filter((desc) => desc)
    )
  );

  // ✅ Filter data by payment type, selected doctor, and selected service before rendering and totals
  const filteredData =
    data?.length > 0
      ? data.filter(
        (billing) => {
          const matchPayment = paymentType
            ? (billing?.paymentType || t("cash")).toLowerCase() === paymentType.toLowerCase()
            : true;

          const doctorName = `${billing?.billReceivedById?.firstName || ""} ${billing?.billReceivedById?.lastName || ""}`.trim();
          const matchDoctor = selectedDoctor
            ? doctorName === selectedDoctor
            : true;

          const matchService = selectedService
            ? (billing?.serviceItems || []).some(
              (item) => item?.serviceDescription?.trim() === selectedService
            )
            : true;

          return matchPayment && matchDoctor && matchService;
        }
      ).map((billing) => {
        if (selectedService) {
          const matchedServices = (billing?.serviceItems || []).filter(
            (item) => item?.serviceDescription?.trim() === selectedService
          );
          if (matchedServices.length > 0 && billing.totalAmount > 0) {
            const serviceTotal = matchedServices.reduce((sum, item) => sum + (item.totalAmount || 0), 0);
            const ratio = serviceTotal / billing.totalAmount;
            return {
              ...billing,
              serviceItems: matchedServices,
              totalAmount: Number(serviceTotal.toFixed(2)),
              amountReceived: Number((billing.amountReceived * ratio).toFixed(2)),
              outstandingAmount: Number((billing.outstandingAmount * ratio).toFixed(2))
            };
          }
        }
        return billing;
      })
      : [];

  const totalAmount =
    filteredData && filteredData.length > 0
      ? filteredData.reduce((sum, billing) => sum + (billing?.totalAmount || 0), 0)
      : 0;

  const totalReceived =
    filteredData && filteredData.length > 0
      ? filteredData.reduce((sum, billing) => sum + (billing?.amountReceived || 0), 0)
      : 0;

  const totalOutstanding =
    filteredData && filteredData.length > 0
      ? filteredData.reduce((sum, billing) => sum + (billing?.outstandingAmount || 0), 0)
      : 0;

  const onPrintHandler = () => {


    // Create a hidden iframe for printing
    let printFrame = document.getElementById("printFrame");
    if (!printFrame) {
      printFrame = document.createElement("iframe");
      printFrame.id = "printFrame";
      printFrame.style.display = "none";
      document.body.appendChild(printFrame);
    }

    const tableClone = document.querySelector("#billingReport").cloneNode(true);
    // Remove Action Column from Clone
    const actionHeader = tableClone.querySelector("th:last-child");
    if (actionHeader) actionHeader.remove();
    tableClone.querySelectorAll("tr").forEach(tr => {
      const lastCell = tr.querySelector("td:last-child");
      if (lastCell) lastCell.remove();
    });

    const printContent = `
      <html>
        <head>
          <title>IPD Billing Statements Report - ${new Date().toLocaleDateString()}</title>
          <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" rel="stylesheet">
          <style>
            @page { 
              size: ${selectedPageSize} ${selectedOrientation}; 
              margin: 10mm; 
            }
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              padding: 20px; 
              color: #333; 
              background: #fff !important;
            }
            .header { text-align: center; border-bottom: 3px solid var(--primary-color); padding-bottom: 15px; margin-bottom: 25px; }
            .hospital-name { font-size: 28px; font-weight: bold; color: var(--primary-color); margin-bottom: 5px; text-transform: uppercase; }
            .hospital-info { font-size: 14px; color: #555; font-weight: 500; }
            .report-title { font-size: 22px; font-weight: bold; margin-top: 20px; text-transform: uppercase; color: #333; letter-spacing: 1px; }
            .meta-info { display: flex; justify-content: space-between; margin-bottom: 20px; font-size: 14px; color: #444; border-bottom: 1px dashed #ccc; padding-bottom: 10px; }
            table { width: 100%; border-collapse: collapse !important; border: 1px solid #dee2e6 !important; margin-top: 10px; }
            th { background-color: var(--primary-color) !important; color: white !important; padding: 12px 8px !important; text-align: center !important; font-size: 14px; border: 1px solid var(--primary-hover) !important; text-transform: uppercase; }
            td { padding: 10px 8px !important; border: 1px solid #dee2e6 !important; font-size: 12px; vertical-align: middle !important; text-align: center; color: #222; }
            tr:nth-child(even) { background-color: #f8fbfb !important; }
            tfoot tr td { background-color: var(--light-color) !important; font-weight: bold !important; border-top: 2px solid var(--primary-color) !important; color: var(--primary-color) !important; font-size: 14px; }
            .footer { position: fixed; bottom: 0; width: 100%; text-align: center; font-size: 12px; color: #777; border-top: 1px solid #ddd; padding-top: 8px; font-style: italic; }
            @media print { 
              .no-print { display: none !important; } 
              body { padding: 0; margin: 0; }
              .header { -webkit-print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          ${getReportHeader(hospitalData, t("IPD Billing Statements Report"))}
          <div class="meta-info">
            <div><strong>${t("Report Date")}:</strong> ${new Date().toLocaleDateString()}</div>
            <div><strong>${t("Total Records")}:</strong> ${filteredData?.length || 0}</div>
          </div>
          <div class="table-container">
            ${tableClone.outerHTML}
          </div>
        </body>
      </html>
    `;

    const frameDoc = printFrame.contentWindow.document;
    frameDoc.open();
    frameDoc.write(printContent);
    frameDoc.close();

    printFrame.contentWindow.focus();
    setTimeout(() => {
      printFrame.contentWindow.print();
    }, 500);
  };

  const handleExport = () => {
    if (exportFormat === "excel") {
      setIsExporting(true);
      setTimeout(() => {
        exportTableToExcel("billingReport", "IPD_Billing_Report");
        setIsExporting(false);
      }, 500);
    } else {
      onGetPDfHandler();
    }
  };
  const onGetPDfHandler = () => {
    const pdfDoc = new jsPDF(selectedOrientation, "pt", selectedPageSize);
    const pageWidth = pdfDoc.internal.pageSize.getWidth();

    const startY = drawReportHeader(pdfDoc, hospitalData, "IPD Billing Report", { margin: 0, top: 0, hideSeparator: true, isExport: true });

    pdfDoc.autoTable({
      head: [[t("Date"), t("Name"), t("Mobile"), t("Payment Type"), t("Received By"), t("Net Amount"), t("Amount Received"), t("Outstanding")]],
      body: filteredData.map((billing) => [
        billing?.billingDate ?? "-",
        `${billing?.patientId?.firstName || ""} ${billing?.patientId?.lastName || ""}`,
        billing?.patientId?.mobile || "-",
        billing?.paymentType || "Cash",
        `${billing?.billReceivedById?.firstName || ""} ${billing?.billReceivedById?.lastName || ""}`.trim() || "-",
        billing?.totalAmount ?? "0",
        billing?.amountReceived ?? "0",
        billing?.outstandingAmount ?? "0"
      ]),
      startY: startY + 10,
      theme: 'grid',
      styles: {
        fontSize: 8,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        textColor: [0, 0, 0]
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
        halign: 'left'
      },
      margin: { left: 40, right: 40 }
    });

    drawAuthorizedSignatory(pdfDoc, hospitalData, "Authority Sign / Doctor Sign");
    pdfDoc.save("billingreport.pdf");
  };

  const onPreviewHandler = () => {
    const pdfDoc = new jsPDF(selectedOrientation, "pt", selectedPageSize);
    const pageWidth = pdfDoc.internal.pageSize.getWidth();

    const startY = drawReportHeader(pdfDoc, hospitalData, "IPD Billing Report", { margin: 0, top: 0, hideSeparator: true, isExport: true });

    pdfDoc.autoTable({
      head: [[t("Date"), t("Name"), t("Mobile"), t("Payment Type"), t("Received By"), t("Net Amount"), t("Amount Received"), t("Outstanding")]],
      body: filteredData.map((billing) => [
        billing?.billingDate ?? "-",
        `${billing?.patientId?.firstName || ""} ${billing?.patientId?.lastName || ""}`,
        billing?.patientId?.mobile || "-",
        billing?.paymentType || "Cash",
        `${billing?.billReceivedById?.firstName || ""} ${billing?.billReceivedById?.lastName || ""}`.trim() || "-",
        billing?.totalAmount ?? "0",
        billing?.amountReceived ?? "0",
        billing?.outstandingAmount ?? "0"
      ]),
      startY: startY + 10,
      theme: 'grid',
      styles: {
        fontSize: 8,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        textColor: [0, 0, 0]
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
        halign: 'left'
      },
      margin: { left: 40, right: 40 }
    });

    drawAuthorizedSignatory(pdfDoc, hospitalData, "Authority Sign / Doctor Sign");

    const pdfBlob = pdfDoc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    setShowPreview(pdfUrl);
  };

  return (
    <>
      {isLoading && <Loader />}
      <div className="container-fluid">
        <div className="bg-light p-3 rounded shadow-sm border mb-4 d-flex justify-content-between align-items-center">
          <h5 className="textReports mb-0">{t("Billing Statements")}</h5>
        </div>
        <Filters onGetStartAndEndDate={onGetStartEndDateHandler} />

        {/* 🔹 Filters Row */}
        <div className="bg-light p-3 rounded shadow-sm border mb-4 mt-3 mx-0">
          <div className="row align-items-center gy-3">
            <div className="col-md-2 col-12">
              <div className="form-check d-flex align-items-center">
                <input
                  type="checkbox"
                  id="outstandingOnly"
                  className="form-check-input"
                  checked={outstandingOnly}
                  onChange={(e) => {
                    setSearchPara("");
                    setSelectedPatient(null);
                    setPaymentType("");
                    setSelectedDoctor("");
                    setSelectedService("");
                    setOutstandingOnly(e.target.checked);
                  }}
                  style={{ cursor: 'pointer' }}
                />
                <label className="form-check-label fw-bold text-muted ms-2 mb-0" htmlFor="outstandingOnly">
                  {t("Outstanding Only")}
                </label>
              </div>
            </div>

            <div className="col-md-3 col-12">
              <form onSubmit={onSearchPatientHandler} className="d-flex w-100">
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("Search Name or Mobile")}
                  value={searchPara}
                  onChange={(e) => setSearchPara(e.target.value)}
                  required
                  style={{ border: "1px solid #ced4da", borderRadius: "4px 0 0 4px" }}
                />
                <button type="submit" className="btn-premium" style={{ borderRadius: "0 4px 4px 0" }}>
                  <FaSearch />
                </button>
              </form>
            </div>

            <div className="col-md-2 col-12">
              <div className="d-flex align-items-center gap-2">
                <label className="mb-0 fw-bold text-muted text-nowrap">{t("Payment")}:</label>
                <select
                  className="form-select themed-select"
                  value={paymentType}
                  onChange={(e) => setPaymentType(e.target.value)}
                  style={{ border: "1px solid #ced4da" }}
                >
                  <option value="">-- {t("Payment")} --</option>
                  <option value="cash">{t("Cash")}</option>
                  <option value="card">{t("Card")}</option>
                  <option value="upi">{t("UPI")}</option>
                </select>
              </div>
            </div>

            <div className="col-md-2 col-12">
              <div className="d-flex align-items-center gap-2">
                <label className="mb-0 fw-bold text-muted text-nowrap">{t("Received By")}:</label>
                <select
                  className="form-select themed-select"
                  value={selectedDoctor}
                  onChange={(e) => setSelectedDoctor(e.target.value)}
                  style={{ border: "1px solid #ced4da" }}
                >
                  <option value="">-- {t("Received By")} --</option>
                  {uniqueDoctors.map((doc, idx) => (
                    <option key={idx} value={doc}>
                      {doc}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="col-md-3 col-12">
              <div className="d-flex align-items-center gap-2">
                <label className="mb-0 fw-bold text-muted text-nowrap">{t("Service")}:</label>
                <select
                  className="form-select themed-select"
                  value={selectedService}
                  onChange={(e) => setSelectedService(e.target.value)}
                  style={{ border: "1px solid #ced4da" }}
                >
                  <option value="">-- {t("Service")} --</option>
                  {uniqueServices.map((service, idx) => (
                    <option key={idx} value={service}>
                      {service}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {isError && <p className="text-danger mt-2">{t("Error")}: {error}</p>}

        <div className="printData boxes table-responsive mt-3">
          <Table id="billingReport" striped bordered hover>
            <TableHeader />
            <tbody>
              {filteredData && filteredData.length > 0 ? (
                filteredData.map((billing, idx) => (
                  <tr key={billing?.id || idx}>
                    <td>{billing?.billingDate ?? "-"}</td>
                    <td>
                      {billing?.patientId?.firstName +
                        " " +
                        billing?.patientId?.lastName}
                    </td>
                    <td>{billing?.patientId?.mobile}</td>
                    <td>{billing?.paymentType || t("Cash")}</td>
                    <td>
                      {billing?.billReceivedById?.firstName ||
                        billing?.billReceivedById?.lastName
                        ? `${billing?.billReceivedById?.firstName || ""} ${billing?.billReceivedById?.lastName || ""
                          }`.trim()
                        : "-"}
                    </td>
                    <td>{billing?.totalAmount ?? "-"}</td>
                    <td>{billing?.amountReceived ?? "-"}</td>
                    <td>{billing?.outstandingAmount ?? "-"}</td>
                    <td>
                      <div className="btnconatiner">
                        <Button
                          size="sm"
                          className="btn-outline-secondary-premium px-2"
                          title={t("Print Bill")}
                          disabled={!!selectedService}
                          onClick={() => {
                            printBillStatementReceipt(
                              billing?.patientId,
                              billing?.serviceItems || [],
                              billing?.totalAmount,
                              billing?.amountReceived,
                              billing?.outstandingAmount,
                              billing?.billingDate,
                              billing?.paymentType,
                              billing?.txnNo,
                              hospitalData,
                              billing?.id
                            );
                          }}
                        >
                          <FaPrint />
                        </Button>
                        <Button
                          size="sm"
                          className="btn-outline-export-premium px-2"
                          title={t("Export Bill")}
                          disabled={!!selectedService}
                          onClick={() => {
                            exportBillPDF(
                              billing?.patientId,
                              billing?.serviceItems || [],
                              billing?.totalAmount,
                              billing?.amountReceived,
                              billing?.outstandingAmount,
                              billing?.billingDate,
                              billing?.paymentType,
                              billing?.txnNo,
                              "a4",
                              hospitalData,
                              billing?.id
                            );
                          }}
                        >
                          <FaFileExport />
                        </Button>
                        <Button
                          size="sm"
                          className="px-2"
                          style={{ backgroundColor: '#25D366', borderColor: '#25D366', color: 'white' }}
                          title={t("WhatsApp Bill")}
                          disabled={!!selectedService}
                          onClick={() => {
                            shareBillViaWhatsApp(
                              billing?.patientId,
                              billing?.serviceItems || [],
                              billing?.totalAmount,
                              billing?.amountReceived,
                              billing?.outstandingAmount,
                              billing?.billingDate,
                              billing?.paymentType,
                              billing?.txnNo,
                              hospitalData,
                              billing?.id
                            );
                          }}
                        >
                          <FaWhatsapp />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="text-center">
                    {t("No Record found")}
                  </td>
                </tr>
              )}
            </tbody>

            {filteredData && filteredData.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan="5" className="text-end">
                    <strong>{t("Total")}</strong>
                  </td>
                  <td>
                    <strong>{totalAmount.toFixed(2)}</strong>
                  </td>
                  <td>
                    <strong>{totalReceived.toFixed(2)}</strong>
                  </td>
                  <td>
                    <strong>{totalOutstanding.toFixed(2)}</strong>
                  </td>
                  <td>

                  </td>
                </tr>
              </tfoot>
            )}
          </Table>
        </div>

        {filteredData && filteredData.length > 0 && (
          <div className="options mt-4 bg-light p-3 rounded shadow-sm border mx-0">
            <div className="row g-3 justify-content-center align-items-end">
              <div className="col-md-auto col-6">
                <label className="fw-bold small d-block">{t("Select Size")}</label>
                <select
                  className="form-select themed-select"
                  value={selectedPageSize}
                  onChange={(e) => setSelectedPageSize(e.target.value)}
                >
                  <option value="a2">A2</option>
                  <option value="a3">A3</option>
                  <option value="a4">A4</option>
                  <option value="a5">A5</option>
                </select>
              </div>

              <div className="col-md-auto col-6">
                <label className="fw-bold small d-block">{t("Orientation")}</label>
                <select
                  className="form-select themed-select"
                  value={selectedOrientation}
                  onChange={(e) => setSelectedOrientation(e.target.value)}
                >
                  <option value="portrait">{t("Portrait")}</option>
                  <option value="landscape">{t("Landscape")}</option>
                </select>
              </div>

              <div className="col-md-auto col-6">
                <label className="fw-bold small d-block mb-1">&nbsp;</label>
                <Button onClick={onPrintHandler} className="btn-premium w-100">
                  <FaPrint className="me-2" /> {t("PRINT")}
                </Button>
              </div>

              <div className="col-md-auto col-6">
                <label className="fw-bold small d-block mb-1">&nbsp;</label>
                <Button onClick={onPreviewHandler} className="btn-premium w-100">
                  <FaEye className="me-2" /> {t("PREVIEW")}
                </Button>
              </div>

              <div className="col-md-auto col-12 mt-3 mt-md-0">
                <div className="d-flex gap-2">
                  <div className="flex-grow-1">
                    <label className="fw-bold small d-block">{t("Format")}</label>
                    <select
                      className="form-select themed-select"
                      value={exportFormat}
                      onChange={(e) => setExportFormat(e.target.value)}
                    >
                      <option value="pdf">{t("PDF Format")}</option>
                      <option value="excel">{t("Excel Format")}</option>
                    </select>
                  </div>
                  <div className="align-self-end">
                    <Button
                      onClick={handleExport}
                      className="btn-export-premium d-flex align-items-center"
                      disabled={isExporting}
                    >
                      {isExporting ? (
                        <Spinner animation="border" size="sm" className="me-2" />
                      ) : (
                        <FaFileExport className="me-2" />
                      )}
                      {isExporting ? t("EXPORTING...") : t("EXPORT")}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ✅ Patient Modal */}
        <Modal
          show={patientData?.length > 0}
          onHide={onDataCloseHandler}
          centered
          size="lg"
          className="patientModel"
        >
          <Modal.Header
            closeButton
            className="modal-header-themed"
          >
            <Modal.Title className="w-100 text-center text-white" style={{ color: 'white' }}>
              {t("Select Patient")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>{t("Patient Id")}</th>
                  <th>{t("Name")}</th>
                  <th>{t("Mobile")}</th>
                  <th>{t("Action")}</th>
                </tr>
              </thead>
              <tbody>
                {patientData.map((p) => (
                  <tr key={p.patientId}>
                    <td>{p.patientId}</td>
                    <td>
                      {`${p.firstName || ""} ${p.middleName ? p.middleName + " " : ""}${p.lastName || ""}`.trim()}
                    </td>
                    <td>{p.mobile || "-"}</td>
                    <td>
                      <Button
                        className="btn-select-premium"
                        onClick={() => getSelectedPatientFromList(p)}
                      >
                        <FaCheck className="me-1" /> {t("Select")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Modal.Body>
        </Modal>

        {showPreview && (
          <div
            className="modal fade show"
            style={{ display: "block", backgroundColor: 'rgba(0,0,0,0.6)' }}
            tabIndex="-1"
            role="dialog"
          >
            <div className="modal-dialog modal-xl modal-dialog-centered" role="document">
              <div className="modal-content shadow-lg border-0">
                <div className="modal-header modal-header-themed" style={{ backgroundColor: 'var(--primary-color)', color: 'white' }}>
                  <h5 className="modal-title d-flex align-items-center" style={{ color: 'white' }}>
                    <FaEye className="me-2" /> {t("IPD BILLING STATEMENTS PREVIEW")}
                  </h5>
                  <button
                    type="button"
                    className="btn btn-link text-white p-0 border-0"
                    onClick={() => setShowPreview(null)}
                    style={{ fontSize: "1.5rem", lineHeight: "1" }}
                  >
                    <FaTimes />
                  </button>
                </div>
                <div className="modal-body p-0 bg-dark">
                  <iframe
                    title="PDF Preview"
                    src={showPreview}
                    width="100%"
                    height="750"
                    frameBorder="0"
                    className="w-100"
                  ></iframe>
                </div>
                <div className="modal-footer bg-light p-2">
                  <Button className="btn-secondary-premium" onClick={() => setShowPreview(null)}>
                    {t("CLOSE PREVIEW")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div >
    </>
  );
};

export default BillStatements;